import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Edit2, Trash2, Award } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useUserAwards, UserAward } from '@/hooks/useUserAwards';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { AwardForm } from './AwardForm';

interface AwardsSectionProps {
  userId?: string;
  isEditing?: boolean;
}

export const AwardsSection = ({ userId, isEditing = false }: AwardsSectionProps) => {
  const { t, i18n } = useTranslation('ui');
  const { awards, loading, addAward, updateAward, deleteAward } = useUserAwards(userId);
  const toast = useTranslatedToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    issuer: '',
    date: '',
    description: ''
  });

  const resetForm = useCallback(() => {
    setFormData({
      title: '',
      issuer: '',
      date: '',
      description: ''
    });
  }, []);

  const loadFormData = useCallback((award: UserAward) => {
    setFormData({
      title: award.title || '',
      issuer: award.issuer || '',
      date: award.date || '',
      description: award.description || ''
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const dataToSave = {
        ...formData,
        issuer: formData.issuer || null,
        date: formData.date || null,
        description: formData.description || null
      };

      if (editingId) {
        await updateAward(editingId, dataToSave);
        toast.success('profile_sections.award_updated');
        setEditingId(null);
      } else {
        await addAward(dataToSave);
        toast.success('profile_sections.award_added');
        setShowAddForm(false);
      }
      resetForm();
    } catch (err) {
      toast.error('profile_sections.award_save_failed');
    }
  }, [formData, editingId, updateAward, addAward, toast, resetForm]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteAward(id);
      toast.success('success.deleted');
    } catch (err) {
      toast.error('error.generic');
    }
  }, [deleteAward, toast]);

  const handleFormDataChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    if (editingId) {
      setEditingId(null);
    } else {
      setShowAddForm(false);
    }
    resetForm();
  }, [editingId, resetForm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    // 根据当前语言设置本地化格式
    const languageMap: { [key: string]: string } = {
      'en': 'en-US',
      'es': 'es-ES', 
      'zh': 'zh-CN',
      'fr': 'fr-FR',
      'ja': 'ja-JP'
    };
    
    const locale = languageMap[i18n.language] || 'en-US';
    
    return new Date(dateString).toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">{t('profile_sections.loading_awards')}</div>;
  }


  return (
    <div className="space-y-4">
      {isEditing && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddForm(true)}
            disabled={showAddForm || editingId !== null}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('profile_sections.add_award')}
          </Button>
        </div>
      )}

      {(showAddForm || editingId) && (
        <AwardForm
          formData={formData}
          onFormDataChange={handleFormDataChange}
          onSave={handleSave}
          onCancel={handleCancel}
          editingId={editingId || undefined}
        />
      )}

      {awards.length === 0 && !showAddForm ? (
        <div className="py-12 text-center">
          <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('profile_sections.no_awards_title')}</h3>
          <p className="text-muted-foreground text-sm">
            {t('profile_sections.no_awards_description')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {awards.map((award) => {
            if (editingId === award.id) return null;

            return (
              <div key={award.id} className="border rounded-lg p-4">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Award title as primary heading */}
                      <h3 className="text-lg font-bold text-foreground mb-1">
                        {award.title}
                      </h3>
                      
                      {/* Issuer as secondary text */}
                      {award.issuer && (
                        <p className="text-base text-muted-foreground mb-3">
                          {award.issuer}
                        </p>
                      )}
                      
                      {/* Tags for date */}
                      <div className="flex items-center flex-wrap gap-2 text-xs mb-4">
                        {award.date && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            <Award className="w-3 h-3 text-yellow-500" />
                            {t('profile_sections.received')}: {formatDate(award.date)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {isEditing && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingId(award.id);
                            loadFormData(award);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(award.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Description with proper typography */}
                  {award.description && (
                    <div className="space-y-2">
                      <p className="text-sm text-foreground leading-relaxed">
                        {award.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};